import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Divider } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  GetStorageAreaPayload,
  GetStorageAreas,
  StorageAreaPackages,
} from "../../../../services/storage-area/get/types";
import { getStorageArea } from "../../../../services/storage-area/get";
import { theme } from "../../../../helpers/theme";
import { LoadingBackDrop } from "../../../../components/loader";
import { GoBackButton } from "../../../../components/GoBackButton";
import { StyledButton } from "../../../../components/Button/button";
import { StyledTable } from "../../../../components/table/styled-table";
import { Summary } from "../../../operator-views/operator-orders-details/components/Summary";
import {
  getFiscalBreakdownEnum,
  getLengthUnitEnum,
  getPackageTypeEnum,
  getStorageType,
  getWeightUnitEnum,
} from "../../../operator-views/operator-subcontractors-storage-area/helpers";
import {
  getPlaceEnum,
  getTemperatureConditionsEnum,
} from "../../../operator-views/operator-orders-offering/helpers";

const SubcontractorStorageArea = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [storageArea, setStorageArea] = useState<GetStorageAreaPayload>();

  const StorageAreaGetMutation = useQuery(
    ["StorageArea"],
    () => getStorageArea(id),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,

      onSuccess(data) {
        setStorageArea(data.data.payload);
      },
    }
  );

  const storageAreaColumns: GridColDef[] = [
    {
      field: "packageType",
      headerName: "Rodzaj opakowania",
      minWidth: 200,
      flex: 1,
      hideable: false,
    },
    {
      field: "length",
      headerName: "Długość",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "lengthMin",
      headerName: "Min",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "lengthMax",
      headerName: "Max",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "width",
      headerName: "Szerokość",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "widthMin",
      headerName: "Min",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "widthMax",
      headerName: "Max",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "height",
      headerName: "Wysokość",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "heightMin",
      headerName: "Min",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "heightMax",
      headerName: "Max",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "weight",
      headerName: "Waga",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "weightMin",
      headerName: "Min",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "weightMax",
      headerName: "Max",
      minWidth: 100,
      flex: 1,
    },
  ];
  const getRowsToStorageAreaTable = (index: number) => {
    const rows: any = storageArea?.obszary_skladowania[index].opakowania.map(
      (r: StorageAreaPackages) => ({
        id: r.id_opakowanie,
        packageType: getPackageTypeEnum(r.rodzaj_opakowania),
        length: `${
          r.dlugosc ? r.dlugosc + getLengthUnitEnum(r.jednostka_dlugosci) : "-"
        }`,
        lengthMin: `${
          r.dlugosc_min
            ? r.dlugosc_min + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        lengthMax: `${
          r.dlugosc_max
            ? r.dlugosc_max + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        width: `${
          r.szerokosc
            ? r.szerokosc + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        widthMin: `${
          r.szerokosc_min
            ? r.szerokosc_min + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        widthMax: `${
          r.szerokosc_max
            ? r.szerokosc_max + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        height: `${
          r.wysokosc
            ? r.wysokosc + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        heightMin: `${
          r.wysokosc_min
            ? r.wysokosc_min + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        heightMax: `${
          r.wysokosc_max
            ? r.wysokosc_max + getLengthUnitEnum(r.jednostka_dlugosci)
            : "-"
        }`,
        weight: `${
          r.waga ? r.waga + getWeightUnitEnum(r.jednostka_wagi) : "-"
        }`,
        weightMin: `${
          r.waga_min ? r.waga_min + getWeightUnitEnum(r.jednostka_wagi) : "-"
        }`,
        weightMax: `${
          r.waga_max ? r.waga_max + getWeightUnitEnum(r.jednostka_wagi) : "-"
        }`,
      })
    );

    if (rows) return rows;
  };

  if (StorageAreaGetMutation.isLoading || StorageAreaGetMutation.isRefetching) {
    return <LoadingBackDrop loading={true} />;
  }
  return (
    <StorageAreaContainer>
      <NavButtonsWrapper>
        <GoBackButton description={"Powrót"} />
        <StyledButton
          style={{ width: "120px" }}
          onClick={() =>
            navigate(
              `/subcontractor/subcontractors/storage-area-availability/${storageArea?.id_magazyn}`
            )
          }
        >
          Dostępność
        </StyledButton>
      </NavButtonsWrapper>
      {storageArea?.obszary_skladowania.map((item: GetStorageAreas, index) => (
        <>
          <SectionsDivider />
          <StorageWithPackagesWrapper>
            <StorageAreaSection>
              <SectionHeaderWrapper>
                <SectionTitle>{`Obszary składowania: ${storageArea?.kod_magazynu}`}</SectionTitle>
              </SectionHeaderWrapper>

              <Divider sx={{ marginBottom: "26px" }} />
              <SummaryTableContainer>
                <Summary
                  data={[
                    {
                      propertyKey: item.kod,
                      propertyName: "Kod",
                      propertyValue: item.kod,
                    },
                    {
                      propertyKey: getStorageType(item.typ_skladowania),
                      propertyName: "Typ składowania",
                      propertyValue: getStorageType(item.typ_skladowania),
                    },
                    {
                      propertyKey: getPlaceEnum(item.miejsce),
                      propertyName: "Miejsce",
                      propertyValue: getPlaceEnum(item.miejsce),
                    },
                    {
                      propertyKey: getFiscalBreakdownEnum(
                        item.podzial_fiskalny
                      ),
                      propertyName: "Podział fiskalny",
                      propertyValue: getFiscalBreakdownEnum(
                        item.podzial_fiskalny
                      ),
                    },
                    {
                      propertyKey: getTemperatureConditionsEnum(
                        item.warunek_temperaturowy
                      ),
                      propertyName: "Warunek temp.",
                      propertyValue: getTemperatureConditionsEnum(
                        item.warunek_temperaturowy
                      ),
                    },
                  ]}
                />
              </SummaryTableContainer>
            </StorageAreaSection>
            {item.opakowania.length > 0 && (
              <StorageAreaTableContianer>
                <StyledTable
                  disableExtendRowFullWidth={true}
                  rows={getRowsToStorageAreaTable(index)}
                  columns={storageAreaColumns}
                  pageSize={item.opakowania.length}
                  disableSelectionOnClick={true}
                  autoHeight
                />
              </StorageAreaTableContianer>
            )}
          </StorageWithPackagesWrapper>
        </>
      ))}
    </StorageAreaContainer>
  );
};

export default SubcontractorStorageArea;

const StorageAreaContainer = styled.div`
  @media (min-width: 1024px) {
    margin-left: 320px;
    margin-right: 30px;
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 20px;
    margin-right: 20px;
  }
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const StorageAreaSection = styled.section`
  background: ${theme.colors.white};
  max-width: 800px;

  border-radius: 8px;
  padding: 32px;
  margin-bottom: 20px;
  @media (max-width: 500px) {
    padding: 20px 30px;
  }
`;
const StorageWithPackagesWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const StorageAreaTableContianer = styled.div`
 width: 100%;
 display: flex;
 justify-content: center;
 margin 20px 0;
`;
const SectionTitle = styled.div`
  font-family: ${theme.fonts.interRegular} !important;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: black;
  font-family: "Inter";
  color: ${theme.colors.black_primaryText};
  @media (max-width: 500px) {
    margin-top: 16px;
  }
`;
const SectionHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;
const SummaryTableContainer = styled.div`
  display: flex;
  gap: 30px;
`;
const NavButtonsWrapper = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
  width: 100%;

  //max-width: 800px;
`;
const SectionsDivider = styled.hr`
  width: 100%;
  height: 0.2px;
  margin-bottom: 30px;
  background-color: black;
`;
